// src/components/TtsStreamer.tsx
import { forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import { calculateTTSCost, updateUserSpendingV2 } from '../utils/TokenUtils';
import { useDynamoUtils } from '../context/DynamoUtilsContext';

const TTS_PROXY_URL = process.env.REACT_APP_TTS_PROXY_URL;

/**
 * @typedef {Object} TtsStreamerHandles
 * @property {function(string): Promise<void>} speak - Function to speak text
 * @property {function(): void} stop - Function to stop speaking
 */

/**
 * Component that handles text-to-speech streaming
 * @type {React.ForwardRefExoticComponent<React.RefAttributes<TtsStreamerHandles>>}
 */
const TtsStreamer = forwardRef(({ userId, onStateChange }, ref) => {
  const audioRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const streamReaderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioBufferSourceNodeRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const appendQueue = useRef([]);
  const dynamoUtilsRef = useDynamoUtils();
  const isAppendingRef = useRef(false);

  const cleanUpMSE = () => {
    if (audioRef.current && audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
      URL.revokeObjectURL(audioRef.current.src);
    }
    if (audioRef.current) {
        audioRef.current.removeAttribute('src');
        audioRef.current.load(); // Reset
    }
    if (mediaSourceRef.current) {
      if (mediaSourceRef.current.readyState === 'open' && sourceBufferRef.current && !sourceBufferRef.current.updating) {
        try { mediaSourceRef.current.endOfStream(); } 
        catch (e) { console.warn('TTS: Error during MediaSource endOfStream on cleanup:', e); }
      }
      // Detach listeners if they were added manually to mediaSourceRef.current directly
      // mediaSourceRef.current.removeEventListener('sourceopen', ...);
    }
    mediaSourceRef.current = null;
    sourceBufferRef.current = null;
    appendQueue.current = [];
    isAppendingRef.current = false;
  };

  const cleanUpFallback = () => {
    if (audioBufferSourceNodeRef.current) {
      try {
        audioBufferSourceNodeRef.current.stop();
      } catch(e) {
        // console.warn("TTS Fallback: Error stopping AudioBufferSourceNode", e);
      }
      audioBufferSourceNodeRef.current.disconnect();
      audioBufferSourceNodeRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(e => console.warn("TTS Fallback: Error closing AudioContext",e));
      audioContextRef.current = null;
    }
  };

  const cleanUp = () => {
    if (streamReaderRef.current) {
      streamReaderRef.current.cancel('Cleanup initiated').catch(e => console.warn("TTS: Error cancelling stream reader on cleanup", e));
      streamReaderRef.current = null;
    }
    cleanUpMSE();
    cleanUpFallback();
    setIsPlaying(false);
    setIsLoading(false);
    setError(null);
    
    // Notify parent of state change
    if (onStateChange) {
      onStateChange({ isLoading: false, isPlaying: false });
    }
  };
  
  const processAppendQueue = () => {
    if (isAppendingRef.current || !sourceBufferRef.current || sourceBufferRef.current.updating || appendQueue.current.length === 0 || mediaSourceRef.current?.readyState !== 'open') {
      return;
    }
    isAppendingRef.current = true;
    const bufferToAppend = appendQueue.current.shift();
    if (bufferToAppend) {
      try {
        sourceBufferRef.current.appendBuffer(bufferToAppend);
      } catch (e) {
        console.error('TTS: Error appending buffer (queued):', e);
        setError(`Error appending buffer: ${e.message}`);
        isAppendingRef.current = false;
        // Consider more robust error handling like full cleanup
        if (e.name === 'QuotaExceededError') {
            console.warn("TTS: QuotaExceededError, trying to remove some buffer.");
            // Implement buffer removal logic if needed, e.g., remove(0, sourceBufferRef.current.buffered.start(0) + 1)
        }
        cleanUp(); // Stop on critical append error
      }
    } else {
      isAppendingRef.current = false;
    }
  };

  /**
   * Play audio stream using Media Source Extensions
   * @param {ReadableStream} stream - Stream of audio data
   * @returns {Promise} Promise that resolves when playback finishes
   */
  const playAudioStreamMSE = (stream) => {
    return new Promise((resolve, reject) => {
      cleanUpMSE(); // Clean up previous MSE state specifically

      const audio = audioRef.current;
      if (!audio) {
        reject(new Error('Audio element not available.'));
        return;
      }

      const mediaSource = new MediaSource();
      mediaSourceRef.current = mediaSource;
      audio.src = URL.createObjectURL(mediaSource); // This should be done here

      const onSourceOpen = async () => {
        console.log('TTS: MediaSource opened.');
        // URL.revokeObjectURL(audio.src); // Spec says revoke after assigning to src if MS is source. Re-evaluating.
                                        // It's safer to revoke when MS is closed or audio element src is changed.
                                        // For now, let's revoke in cleanUpMSE or when it's done.
        if (!mediaSourceRef.current) { // Check if cleanup happened before sourceopen
            reject(new Error("MediaSource became null before sourceopen processing."));
            return;
        }

        try {
          const sb = mediaSourceRef.current.addSourceBuffer('audio/mpeg');
          sourceBufferRef.current = sb;

          sb.addEventListener('updateend', () => {
            isAppendingRef.current = false;
            processAppendQueue();
            if (streamReaderRef.current === null && appendQueue.current.length === 0 && mediaSourceRef.current?.readyState === 'open' && !sb.updating) {
              try { mediaSourceRef.current.endOfStream(); } 
              catch (e) { console.warn("TTS: Error calling endOfStream on updateend:", e); }
            }
          });

          sb.addEventListener('error', (e) => {
            console.error('TTS: SourceBuffer error:', e);
            setError('SourceBuffer error.');
            reject(new Error('SourceBuffer error.'));
            cleanUp();
          });
          
          mediaSourceRef.current?.removeEventListener('sourceopen', onSourceOpen);

          streamReaderRef.current = stream.getReader();
          const reader = streamReaderRef.current;

          audio.play().then(() => {
            setIsLoading(false);
            setIsPlaying(true);
            // Notify parent of state change
            if (onStateChange) {
              onStateChange({ isLoading: false, isPlaying: true });
            }
          }).catch(e => {
            console.error('TTS: Audio play failed:', e);
            if (e.name === 'NotAllowedError') {
              // Show simple alert for autoplay restriction
              alert('AI Audio failed due to browser restrictions. Try toggling the TTS button on and off or refreshing the page.');
              setError('Audio blocked by browser - try toggling TTS button');
            } else {
              setError(`Audio play failed: ${e.message}`);
            }
            setError(`Audio play failed: ${e.message}. User interaction might be required.`);
            setIsLoading(false);
            // Notify parent of error state
            if (onStateChange) {
              onStateChange({ isLoading: false, isPlaying: false });
            }
            reject(e); // Reject promise if play fails
          });

          const readLoop = async () => {
            while (true) {
              if (!streamReaderRef.current) break; // Reader was cancelled
              try {
                const { value, done } = await reader.read();
                if (done) {
                  streamReaderRef.current = null;
                  if (!isAppendingRef.current && appendQueue.current.length === 0 && mediaSourceRef.current?.readyState === 'open' && sourceBufferRef.current && !sourceBufferRef.current.updating) {
                    try { mediaSourceRef.current.endOfStream(); } 
                    catch (e) { console.warn("TTS: Error calling endOfStream after reader done:", e); }
                  }
                  console.log('TTS: Stream finished reading.');
                  break; 
                }
                if (value) {
                  appendQueue.current.push(value);
                  processAppendQueue();
                }
              } catch (readError) {
                console.error('TTS: Error reading from stream:', readError);
                setError(`Stream read error: ${readError.message}`);
                if (mediaSourceRef.current?.readyState === 'open' && sourceBufferRef.current && !sourceBufferRef.current.updating) {
                   try { mediaSourceRef.current.endOfStream(); } catch(eS){ console.warn("TTS: Error ending stream on readError:", eS); }
                }
                reject(readError); // Reject the main promise
                return; 
              }
            }
          };
          readLoop(); // Don't await this, let it run in background

        } catch (e) {
          console.error('TTS: Error in sourceopen or during streaming setup:', e);
          setError(`Streaming setup error: ${e.message}`);
          if (mediaSourceRef.current?.readyState === 'open') {
             try { mediaSourceRef.current.endOfStream(); } catch(eS) { console.warn("TTS: Error ending stream on setup error:", eS); }
          }
          reject(e);
        }
      };

      mediaSource.addEventListener('sourceopen', onSourceOpen);
      mediaSource.addEventListener('sourceended', () => {
        console.log('TTS: MediaSource ended.');
        setIsPlaying(false);
        // Notify parent of state change
        if (onStateChange) {
          onStateChange({ isLoading: false, isPlaying: false });
        }
        resolve(); // Main promise resolved
      });
      mediaSource.addEventListener('sourceclose', () => {
        console.log('TTS: MediaSource closed.');
        // This can be called before sourceended if e.g. src is changed
        // setIsPlaying(false); // Handled by sourceended or error paths
      });

      audio.onended = () => {
        console.log('TTS: HTMLAudioElement playback naturally finished.');
        // setIsPlaying(false); // This is handled by sourceended for MSE
        // resolve(); // This is handled by sourceended for MSE
      };
      audio.onerror = (e) => {
        console.error('TTS: HTMLAudioElement error:', e, audio.error);
        setError(`Audio element error: ${audio.error?.message || 'Unknown audio error'}`);
        reject(audio.error || new Error('Unknown audio element error'));
        cleanUp();
      };
    });
  };

  /**
   * Play audio stream using Web Audio API as fallback
   * @param {ReadableStream} stream - Stream of audio data
   * @returns {Promise} Promise that resolves when playback finishes
   */
  const playAudioStreamFallback = async (stream) => {
    console.log('TTS: Using Web Audio API fallback.');
    cleanUpFallback(); // Clean up previous fallback state

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;
    
    const reader = stream.getReader();
    streamReaderRef.current = reader;
    const chunks = [];
    
    try {
        while (true) {
            if (!streamReaderRef.current) throw new Error("Stream cancelled during fallback read");
            const { value, done } = await reader.read();
            if (done) break;
            if (value) chunks.push(value);
        }
        streamReaderRef.current = null; // Done reading

        if (chunks.length === 0) {
            console.log("TTS Fallback: No audio data received.");
            audioContext.close();
            return;
        }

        const totalLength = chunks.reduce((acc, val) => acc + val.length, 0);
        const concatenatedChunks = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            concatenatedChunks.set(chunk, offset);
            offset += chunk.length;
        }

        const audioBuffer = await audioContext.decodeAudioData(concatenatedChunks.buffer.slice(0)); // Pass a copy
        
        if (audioContext.state === 'closed') { // Check if context was closed (e.g. by stop())
            console.log("TTS Fallback: AudioContext closed before playback could start.");
            return;
        }

        const source = audioContext.createBufferSource();
        audioBufferSourceNodeRef.current = source;
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        
        setIsLoading(false);
        setIsPlaying(true);
        // Notify parent of state change
        if (onStateChange) {
          onStateChange({ isLoading: false, isPlaying: true });
        }
        return new Promise((resolve, reject) => {
            source.onended = () => {
                console.log('TTS Fallback: Audio playback finished.');
                setIsPlaying(false);
                // Notify parent of state change
                if (onStateChange) {
                  onStateChange({ isLoading: false, isPlaying: false });
                }
                cleanUpFallback(); // Includes closing context
                resolve();
            };
            source.start(0);
        });

    } catch (e) {
        console.error('TTS Fallback: Error decoding or playing audio:', e);
        if (e.name === 'NotAllowedError') {
            // Show simple alert for autoplay restriction
            alert('AI Audio failed due to browser restrictions. Try toggling the TTS button on and off or refreshing the page.');
            setError('Audio blocked by browser - try toggling TTS button');
        } else {
            setError(`Fallback audio error: ${e.message}`);
        }
        setError(`Fallback audio error: ${e.message}`);
        cleanUpFallback();
        throw e; // Rethrow to be caught by speak()
    }
  };

  useImperativeHandle(ref, () => ({
    speak: async (text) => {
      if (!text.trim()) {
        console.log('TTS: Empty text, skipping speech.');
        return;
      }
      
      // Function needs to be defined here since it references state
      const stop = async () => {
        console.log('TTS: stop() called.');
        cleanUp();
        return Promise.resolve();
      };
      
      // Stop any ongoing playback before starting a new one.
      await stop(); 
      
      setError(null); // Clear previous errors
      setIsLoading(true); // Set loading state
      
      // Notify parent of loading state
      if (onStateChange) {
        onStateChange({ isLoading: true, isPlaying: false });
      }
      
      console.log('TTS: speak() called with text (first 100 chars):', text.substring(0,100) + "...");

      const token = localStorage.getItem('idToken');
      if (!token) {
        const errMsg = 'TTS: No idToken found in localStorage.';
        console.error(errMsg);
        setError(errMsg);
        setIsLoading(false);
        // Notify parent of error state
        if (onStateChange) {
          onStateChange({ isLoading: false, isPlaying: false });
        }
        return Promise.reject(new Error(errMsg));
      }

      try {
        // Calculate TTS cost before making the request
        const ttsModel = 'gpt-4o-mini-tts'; // or 'tts-1-hd' if using HD model
        const costInfo = calculateTTSCost(text, ttsModel);
        console.log('TTS cost calculation:', costInfo);
        
        const response = await fetch(TTS_PROXY_URL, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text }),
        });

        if (!response.ok) {
          const errorBody = await response.text();
          const errMsg = `TTS: Proxy request failed ${response.status}: ${errorBody.substring(0,200)}`;
          console.error(errMsg);
          setError(errMsg);
          setIsLoading(false);
          // Notify parent of error state
          if (onStateChange) {
            onStateChange({ isLoading: false, isPlaying: false });
          }
          throw new Error(`Proxy request failed with status ${response.status}`);
        }

        if (!response.body) {
          const errMsg = 'TTS: No response body from proxy.';
          console.error(errMsg);
          setError(errMsg);
          setIsLoading(false);
          // Notify parent of error state
          if (onStateChange) {
            onStateChange({ isLoading: false, isPlaying: false });
          }
          throw new Error(errMsg);
        }

        // Track TTS usage after successful request
        if (userId) {
          const dynamoUtils = dynamoUtilsRef.current;
          if (dynamoUtils) {
            console.log('Updating TTS spending:', costInfo);
            updateUserSpendingV2(
              costInfo,
              userId,
              dynamoUtils,
              "Text-to-Speech",
              "tts"
            );
            console.log('TTS spending updated successfully');
          } else {
            console.warn('DynamoDB utils not available for TTS cost tracking');
          }
        } else {
          console.warn('User ID not available for TTS cost tracking');
        }
        
        if ('MediaSource' in window && MediaSource.isTypeSupported('audio/mpeg')) {
            await playAudioStreamMSE(response.body);
        } else {
            console.warn("TTS: MediaSource not supported or audio/mpeg not supported by MediaSource. Using Web Audio API fallback.");
            await playAudioStreamFallback(response.body);
        }
        console.log("TTS: speak() promise resolved (playback may have finished or is ongoing if errors occurred).");

      } catch (err) {
        console.error('TTS: Error in speak() execution:', err);
        setError(err.message || 'An unknown error occurred during TTS initiation.');
        setIsLoading(false);
        // Notify parent of error state
        if (onStateChange) {
          onStateChange({ isLoading: false, isPlaying: false });
        }
        cleanUp(); // Ensure full cleanup on error during speak setup/fetch
        return Promise.reject(err); // Propagate the error
      }
    },
    stop: async () => {
      console.log('TTS: stop() called.');
      // The cleanUp method now handles cancelling reader, MSE, and Fallback audio.
      cleanUp();
      return Promise.resolve();
    },
  }));

  useEffect(() => {
    audioRef.current = new Audio();
    return () => {
      console.log("TTS: TtsStreamer unmounting, running cleanup.");
      if (streamReaderRef.current) {
        streamReaderRef.current.cancel('Cleanup initiated').catch(e => console.warn("TTS: Error cancelling stream reader on cleanup", e));
        streamReaderRef.current = null;
      }
      if (audioRef.current && audioRef.current.src && audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      if (audioRef.current) {
          audioRef.current.removeAttribute('src');
          audioRef.current.load();
      }
      if (mediaSourceRef.current) {
        if (mediaSourceRef.current.readyState === 'open' && sourceBufferRef.current && !sourceBufferRef.current.updating) {
          try { mediaSourceRef.current.endOfStream(); } 
          catch (e) { console.warn('TTS: Error during MediaSource endOfStream on cleanup:', e); }
        }
      }
      mediaSourceRef.current = null;
      sourceBufferRef.current = null;
      appendQueue.current = [];
      isAppendingRef.current = false;
      if (audioBufferSourceNodeRef.current) {
        try {
          audioBufferSourceNodeRef.current.stop();
        } catch(e) {
          // console.warn("TTS Fallback: Error stopping AudioBufferSourceNode", e);
        }
        audioBufferSourceNodeRef.current.disconnect();
        audioBufferSourceNodeRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(e => console.warn("TTS Fallback: Error closing AudioContext",e));
        audioContextRef.current = null;
      }
      if (audioRef.current) {
         // Potentially remove event listeners if added directly, though React handles its own.
        audioRef.current = null;
      }
    };
  }, []);

  // This component does not render anything visible itself.
  return null; 
});

TtsStreamer.displayName = 'TtsStreamer';
export { TtsStreamer };
